/**
 * Iniciais e cor do avatar — o leitor ÚNICO do rosto de uma pessoa.
 *
 * Existia solto em `ClientsIndex.vue` e em `Show.vue`, e a Lista de Processos
 * seria a terceira cópia. Duas cópias da mesma conta viram duas contas
 * diferentes na primeira mudança: se um dia entrar uma sétima cor, ou as
 * iniciais passarem a ignorar "de"/"da", tem que valer em todo lugar.
 *
 * A cor sai do NOME e não da posição na lista: a mesma pessoa tem sempre a
 * mesma cor, em qualquer página, filtro ou ordenação.
 */
export const CORES_DE_AVATAR = [
  'blue',
  'teal',
  'iris',
  'violeta',
  'ambar',
  'ruby',
];

/**
 * Primeira letra do PRIMEIRO e do ÚLTIMO nome — "Maria Helena Souza" vira MS,
 * não MH. É o par que identifica uma pessoa em português; os nomes do meio
 * quase nunca são usados para chamar alguém. A ficha do processo já fazia
 * assim e a lista fazia diferente: a mesma pessoa tinha duas iniciais em duas
 * telas do mesmo produto.
 */
// 🔴 TÍTULO NÃO É NOME (Victor, 17/09/2026). O cartão da conversa mostrava
// só "Dra." como responsável — e o escritório tem mais de uma doutora. O
// nome curto é o título com o primeiro nome ("Dra. Jullya", "Dr.
// Henrique"); palavra de organização some ("Setor Financeiro" é
// Financeiro). As iniciais usam só o nome: "Dra. Jullya Andrade" é JA.
const TITULOS = new Set([
  'dr',
  'dra',
  'doutor',
  'doutora',
  'sr',
  'sra',
  'srta',
  'prof',
  'profa',
  'setor',
  'depto',
  'departamento',
]);

// O título que acompanha o nome curto, na grafia do escritório.
const TITULO_DE_CHAMAR = {
  dr: 'Dr.',
  doutor: 'Dr.',
  dra: 'Dra.',
  doutora: 'Dra.',
};

const palavrasDoNome = nome =>
  String(nome || '')
    // O que vem entre parênteses é anotação ("Fernanda (demo simulado)",
    // "João (filho)"), não nome.
    .replace(/\([^)]*\)/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const chaveDoTitulo = p => p.toLowerCase().replace(/[^\p{L}]/gu, '');

const semTitulos = palavras => {
  const resto = palavras.filter(p => !TITULOS.has(chaveDoTitulo(p)));
  // Só título ("Dra.") fica como está: melhor que nada.
  return resto.length ? resto : palavras;
};

/** O nome de chamar: "Dra. Jullya", "Henrique", "Financeiro". */
export const nomeCurto = nome => {
  const palavras = palavrasDoNome(nome);
  const primeiro = semTitulos(palavras)[0] || '';
  const titulo = palavras
    .map(p => TITULO_DE_CHAMAR[chaveDoTitulo(p)])
    .find(Boolean);
  if (!titulo || primeiro === palavras[0]) return primeiro;
  return `${titulo} ${primeiro}`;
};

export const iniciaisDoNome = nome => {
  // A inicial é a primeira LETRA da palavra, não o primeiro caractere: sem
  // isso a lista mostrava "F(" no avatar.
  const partes = semTitulos(palavrasDoNome(nome))
    .map(parte => (parte.match(/[\p{L}\p{N}]/u) || [''])[0])
    .filter(Boolean);
  if (!partes.length) return '?';
  const primeira = partes[0];
  const ultima = partes.length > 1 ? partes[partes.length - 1] : '';
  return (primeira + ultima).toUpperCase();
};

export const corDoNome = nome => {
  const texto = String(nome || '');
  let soma = 0;
  for (let i = 0; i < texto.length; i += 1) soma += texto.charCodeAt(i);
  return CORES_DE_AVATAR[soma % CORES_DE_AVATAR.length];
};
